import { lockDateKey } from '@/services/lockCalendarDates';
import { lockRateValue } from '@/services/lockNetBuyRate';
import { diffCalendarDays, getBusinessDateString, parseAsLocalDate } from '@/utils/dateHelpers';
import { formatPercent } from '../utils/loanValueHelpers';
import type { ProspectLoan } from '../types';

interface LockStatusCardProps {
  loan: ProspectLoan;
}

// Whole calendar days from today's Pacific date. Flooring the milliseconds
// between local midnights loses a day across a DST change.
function getDaysUntilExpiration(expirationDate: string | undefined | null): number | null {
  const expires = lockDateKey(expirationDate);
  if (!expires) {
    return null;
  }
  return diffCalendarDays(getBusinessDateString(), expires);
}

/**
 * Lock Status card — green left border rule + tinted icon square + LOCKED pill
 * + large green mono rate per the handoff. Class system is defined in
 * `styles.css` under `.lock-status` / `.ls-*`.
 */
export function LockStatusCard({ loan }: LockStatusCardProps) {
  const isLocked = loan.rate_lock_status === 'Locked';
  const days = getDaysUntilExpiration(loan.rate_lock_buy_side_lock_expires);
  const expiresDisplay = loan.rate_lock_buy_side_lock_expires
    ? parseAsLocalDate(loan.rate_lock_buy_side_lock_expires)?.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }) ?? '—'
    : '—';
  const daysLabel =
    days === null
      ? ''
      : days < 0
        ? `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
        : `${days} day${days === 1 ? '' : 's'} remaining`;

  const statusLabel = (loan.rate_lock_status || 'Not Locked').toUpperCase();
  const badgeClass = isLocked ? 'ls-badge' : 'ls-badge unlocked';

  return (
    <section className="lock-status" aria-label="Lock status">
      <div className="ls-left">
        <div className="ls-icon" aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div>
          <div className="ls-title">
            Lock Status
            <span className={badgeClass}>{statusLabel}</span>
          </div>
          <div className="ls-meta">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <span>
              Expires <b>{expiresDisplay}</b>
            </span>
            {daysLabel && (
              <>
                <span className="dot-sep">·</span>
                <span>{daysLabel}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="ls-right">
        <div className="ls-rate-lbl">Interest Rate</div>
        <div className="ls-rate">
          {formatPercent(lockRateValue(loan), 3).replace('%', '')}
          <span className="unit">%</span>
        </div>
      </div>
    </section>
  );
}
